import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import dataService from "../../../helpers/dataService";
import useOperateursAccompagnement from "../../Hooks/useOperateursAccompagnement/useOperateursAccompagnement";
import useOrganizations from "../../Hooks/useOrganizations/useOrganizations";
import { listerSitesDeOperateur } from "../Operateurs/operateursApi";
import {
  UTILISATEUR_VIDE,
  corpsEnregistrement,
  estCompteOperateur,
  estCreation,
  estModifie,
  normaliserUtilisateur,
  sitesBloquants,
  sitesDeOrganisation,
} from "./utilisateurHelpers";

// ============================================================
// useUserDetail — données et actions de la fiche utilisateur. Mêmes appels
// que l'écran historique : GET managers/:id, GET organizations,
// GET organizations/:id/sites (une fois par organisation cochée),
// POST/PATCH managers, renvoi d'activation, (dés)activation, déblocage.
// Un rôle d'opérateur (CE-1407) charge les opérateurs d'accompagnement à la
// place des organisations, puis les sites de la structure choisie
// (GET operateurs/:id/sites) : le compte en garde au moins un (règle du
// 20/10/2026), et le corps envoyé ne porte que sa structure et ses sites.
// ============================================================
export default function useUserDetail({ id, history }) {
  const creation = estCreation(id);
  const [user, setUser] = useState(UTILISATEUR_VIDE);
  const [reference, setReference] = useState(UTILISATEUR_VIDE);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(!creation);
  const [isSaving, setIsSaving] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [isDisabling, setIsDisabling] = useState(false);
  const [isUnlocking, setIsUnlocking] = useState(false);
  const [sites, setSites] = useState([]);
  const [organisationsChargees, setOrganisationsChargees] = useState([]);
  const [organisationsEnChargement, setOrganisationsEnChargement] = useState([]);
  // Tentative de décocher une organisation dont des sites sont encore cochés.
  const [blocage, setBlocage] = useState(null);
  const { organizations, onGetOrganizations } = useOrganizations();
  const { operateurs, onGetOperateurs } = useOperateursAccompagnement();
  const compteOperateur = estCompteOperateur(user);
  // Sites de la structure choisie pour un compte d'opérateur.
  const [sitesStructure, setSitesStructure] = useState({ operateurId: null, sites: [], enChargement: false });

  useEffect(() => {
    onGetOrganizations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (compteOperateur && !operateurs.length) onGetOperateurs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [compteOperateur]);

  useEffect(() => {
    setErrors({});
    setBlocage(null);
    if (creation) {
      setUser(UTILISATEUR_VIDE);
      setReference(UTILISATEUR_VIDE);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    dataService.get(
      `managers/${id}`,
      (datas) => {
        const charge = normaliserUtilisateur(datas);
        setUser(charge);
        setReference(charge);
      },
      setErrors,
      () => setIsLoading(false)
    );
  }, [id, creation]);

  useEffect(() => {
    if (!compteOperateur || !user.operator) {
      setSitesStructure({ operateurId: null, sites: [], enChargement: false });
      return undefined;
    }
    let actif = true;
    const operateurId = user.operator;
    setSitesStructure({ operateurId, sites: [], enChargement: true });
    listerSitesDeOperateur(operateurId).then(
      (datas) => actif && setSitesStructure({ operateurId, sites: Array.isArray(datas) ? datas : [], enChargement: false }),
      () => actif && setSitesStructure({ operateurId, sites: [], enChargement: false })
    );
    return () => {
      actif = false;
    };
  }, [compteOperateur, user.operator]);

  // Les sites se chargent organisation par organisation, une seule fois chacune.
  useEffect(() => {
    if (compteOperateur) return;
    const aCharger = (user.organizations || []).filter((o) => !organisationsChargees.includes(o));
    if (!aCharger.length) return;
    setOrganisationsChargees((prev) => [...prev, ...aCharger]);
    setOrganisationsEnChargement((prev) => [...prev, ...aCharger]);
    aCharger.forEach((organisationId) => {
      dataService.get(
        `organizations/${organisationId}/sites`,
        (datas) =>
          setSites((prev) => [
            ...prev.filter((s) => s.organization !== organisationId),
            ...(Array.isArray(datas) ? datas : []),
          ]),
        () => {},
        () => setOrganisationsEnChargement((prev) => prev.filter((o) => o !== organisationId))
      );
    });
  }, [user.organizations, organisationsChargees, compteOperateur]);

  const modifierChamp = useCallback((name, value) => {
    setUser((u) => ({ ...u, [name]: value }));
  }, []);

  const changer = useCallback(
    (e) => {
      const { name, value, type, checked } = e.target;
      modifierChamp(name, type === "checkbox" ? checked : value);
    },
    [modifierChamp]
  );

  const basculerOrganisation = useCallback(
    (organisationId) => {
      setBlocage(null);
      if (user.organizations.includes(organisationId)) {
        const bloquants = sitesBloquants(sites, organisationId, user.sites);
        if (bloquants.length) {
          setBlocage({
            organisation: organizations.find((o) => o.id === organisationId) || { id: organisationId, name: "" },
            sites: bloquants,
          });
          return;
        }
        modifierChamp(
          "organizations",
          user.organizations.filter((o) => o !== organisationId)
        );
      } else {
        modifierChamp("organizations", [...user.organizations, organisationId]);
      }
    },
    [user.organizations, user.sites, sites, organizations, modifierChamp]
  );

  const basculerSite = useCallback(
    (siteId) => {
      setBlocage(null);
      modifierChamp(
        "sites",
        user.sites.includes(siteId) ? user.sites.filter((s) => s !== siteId) : [...user.sites, siteId]
      );
    },
    [user.sites, modifierChamp]
  );

  // Coche ou décoche tous les sites d'une organisation d'un coup.
  const definirSitesDe = useCallback(
    (organisationId, coche) => {
      setBlocage(null);
      const ids = sitesDeOrganisation(sites, organisationId).map((s) => s.id);
      modifierChamp(
        "sites",
        coche ? Array.from(new Set([...user.sites, ...ids])) : user.sites.filter((s) => !ids.includes(s))
      );
    },
    [sites, user.sites, modifierChamp]
  );

  const enregistrer = useCallback(() => {
    setIsSaving(true);
    setErrors({});
    const sitesConnus =
      !sitesStructure.enChargement && sitesStructure.operateurId === user.operator ? sitesStructure.sites : null;
    const corps = corpsEnregistrement(user, reference, sitesConnus);
    if (creation) {
      dataService.post(
        "managers/",
        corps,
        (datas) => {
          const cree = normaliserUtilisateur(datas);
          setUser(cree);
          setReference(cree);
          toast.success("Utilisateur enregistré");
          if (history && datas && datas.id) history.replace(`/users/${datas.id}`);
        },
        setErrors,
        () => setIsSaving(false)
      );
    } else {
      dataService.patch(
        `managers/${id}`,
        corps,
        (datas) => {
          const maj = normaliserUtilisateur(datas);
          setUser(maj);
          setReference(maj);
          toast.success("Utilisateur enregistré");
        },
        setErrors,
        () => setIsSaving(false)
      );
    }
  }, [creation, id, user, reference, history, sitesStructure]);

  const renvoyerActivation = useCallback(() => {
    if (!window.confirm("Confirmez-vous le renvoi de l'email d'activation ?")) return;
    setIsResending(true);
    dataService.get(
      `managers/${user.id}/renew-activation`,
      () => toast.success("L'email d'activation a bien été envoyé"),
      setErrors,
      () => setIsResending(false)
    );
  }, [user.id]);

  const basculerActivation = useCallback(() => {
    const statut = !user.isActivated;
    if (!window.confirm(`Confirmez-vous ${statut ? "l'activation" : "la désactivation"} de ce compte ?`)) return;
    setIsDisabling(true);
    dataService.patch(
      `managers/${user.id}/activation`,
      { isActivated: statut },
      (datas) => {
        toast.success(statut ? "Le compte a bien été activé" : "Le compte a bien été désactivé");
        const maj = normaliserUtilisateur({ ...user, ...datas });
        setUser(maj);
        setReference(maj);
      },
      setErrors,
      () => setIsDisabling(false)
    );
  }, [user]);

  const debloquer = useCallback(() => {
    setIsUnlocking(true);
    dataService.get(
      `managers/${user.id}/authorization/reset`,
      () =>
        toast.success(
          "Le compte a bien été débloqué, l'utilisateur devra recharger la page de connexion de son côté."
        ),
      setErrors,
      () => setIsUnlocking(false)
    );
  }, [user.id]);

  const modifie = useMemo(() => estModifie(user, reference), [user, reference]);

  return {
    creation,
    user,
    errors,
    isLoading,
    isSaving,
    isResending,
    isDisabling,
    isUnlocking,
    modifie,
    compteOperateur,
    // Structure enregistrée du compte : le lien vers sa fiche n'a de sens que pour elle.
    structureChargee: reference.operator || null,
    operateurs,
    sitesStructure: sitesStructure.sites,
    sitesStructureEnChargement: sitesStructure.enChargement,
    organizations,
    sites,
    organisationsEnChargement,
    blocage,
    fermerBlocage: () => setBlocage(null),
    changer,
    modifierChamp,
    basculerOrganisation,
    basculerSite,
    definirSitesDe,
    enregistrer,
    renvoyerActivation,
    basculerActivation,
    debloquer,
  };
}
